import { useSyncExternalStore } from 'react';
import { createRoot } from 'react-dom/client';
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import { cn } from '../../lib/cn';

export type ToastKind = 'success' | 'error' | 'info';

export interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
}

export interface ToastOptions {
  kind?: ToastKind;
  /** Auto-dismiss delay in ms (default 4000; errors 6000). Pass 0 to keep until dismissed. */
  duration?: number;
}

// ---------------------------------------------------------------------------
// Store — module-level so `toast()` works from anywhere (mutations, loaders)
// without a provider. The <Toaster/> mounts itself into its own React root on
// first use, so no scaffold/App changes are needed.
// ---------------------------------------------------------------------------

let toasts: ToastItem[] = [];
let nextId = 1;
const listeners = new Set<() => void>();
const timers = new Map<number, ReturnType<typeof setTimeout>>();

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): ToastItem[] {
  return toasts;
}

export function dismissToast(id: number) {
  const timer = timers.get(id);
  if (timer) {
    clearTimeout(timer);
    timers.delete(id);
  }
  if (toasts.some((t) => t.id === id)) {
    toasts = toasts.filter((t) => t.id !== id);
    emit();
  }
}

function pushToast(message: string, options?: ToastOptions): number {
  ensureToasterMounted();
  const kind = options?.kind ?? 'info';
  const id = nextId++;
  toasts = [...toasts.slice(-4), { id, kind, message }]; // keep at most 5
  emit();
  const duration = options?.duration ?? (kind === 'error' ? 6000 : 4000);
  if (duration > 0) {
    timers.set(
      id,
      setTimeout(() => dismissToast(id), duration),
    );
  }
  return id;
}

/** Show a toast. `toast.success/error/info` set the kind for you. */
export const toast = Object.assign(
  (message: string, options?: ToastOptions) => pushToast(message, options),
  {
    success: (message: string, options?: Omit<ToastOptions, 'kind'>) =>
      pushToast(message, { ...options, kind: 'success' }),
    error: (message: string, options?: Omit<ToastOptions, 'kind'>) =>
      pushToast(message, { ...options, kind: 'error' }),
    info: (message: string, options?: Omit<ToastOptions, 'kind'>) =>
      pushToast(message, { ...options, kind: 'info' }),
    dismiss: dismissToast,
  },
);

/** Hook flavor of the same API (stable identity — safe in deps arrays). */
export function useToast() {
  return toast;
}

// ---------------------------------------------------------------------------
// Renderer
// ---------------------------------------------------------------------------

const KIND_STYLES: Record<ToastKind, { icon: typeof Info; className: string }> = {
  success: { icon: CheckCircle2, className: 'text-success' },
  error: { icon: AlertTriangle, className: 'text-status-late' },
  info: { icon: Info, className: 'text-accent' },
};

export function Toaster() {
  const items = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  if (items.length === 0) return null;
  return (
    <div
      aria-live="polite"
      className="fixed bottom-4 right-4 z-[100] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2"
    >
      {items.map((item) => {
        const { icon: Icon, className } = KIND_STYLES[item.kind];
        return (
          <div
            key={item.id}
            role="status"
            className="pointer-events-auto flex items-start gap-2.5 rounded-lg border border-grey-200 bg-white px-4 py-3 shadow-elevated animate-fade-in"
          >
            <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', className)} aria-hidden />
            <p className="min-w-0 flex-1 text-body-sm font-medium text-secondary">{item.message}</p>
            <button
              type="button"
              aria-label="Dismiss"
              onClick={() => dismissToast(item.id)}
              className="rounded-md p-0.5 text-grey-400 transition-colors hover:bg-grey-100 hover:text-secondary"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
        );
      })}
    </div>
  );
}

let toasterMounted = false;
function ensureToasterMounted() {
  if (toasterMounted || typeof document === 'undefined') return;
  toasterMounted = true;
  const host = document.createElement('div');
  host.id = 'sf-toaster';
  document.body.appendChild(host);
  createRoot(host).render(<Toaster />);
}
